import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MultiSelectModule } from "primeng/multiselect";
import { CheckboxWrapComponent } from "src/app/shared/components/ui/checkbox-wrap.component";
import { InputWrapComponent } from "src/app/shared/components/ui/input-wrap.component";
import { TaskStatusEnum } from "src/app/shared/models/agent-tasks.model";
import { DataElement } from "src/app/shared/models/common";
import { PresetNote, RXItem } from "src/app/shared/models/order.model";
import { doctorList, rxItems } from "src/app/shared/ngrx/ngrx-selectors";
import { AppState } from "src/app/shared/redux/store";
import { AuthService } from "src/app/shared/services/auth.service";
import { BroshService } from "src/app/shared/services/brosh.service";
import { DataService } from "src/app/shared/services/data.service";
import { SharedService } from "src/app/shared/services/shared.service";

@Component({
  selector: "edit-preset-note",
  templateUrl: "./edit-preset-note.component.html",
  styleUrls: ["./edit-preset-note.component.scss"],
  imports: [
    CommonModule,
    FormsModule,
    InputWrapComponent,
    TranslateModule,
    CheckboxWrapComponent,
    MultiSelectModule,
  ],
  standalone: true,
})
export class EditPresetNoteComponent {
  @Input() note: PresetNote;
  @Output() onCancel = new EventEmitter();
  @Output() onSave = new EventEmitter();

  saving: boolean;

  get isNew(): boolean {
    return !this.note.id;
  }

  get rxItems(): RXItem[] {
    return this.store.selectSignal(rxItems)();
  }

  get doctors(): DataElement[] {
    return this.store.selectSignal(doctorList)();
  }

  get allowEdit(): boolean {
    return this.authService.currentUser.roleCode == "ADMIN";
  }

  constructor(
    private readonly store: Store,
    private broshService: BroshService,
    private authService: AuthService,
    private dataService: DataService,
    private sharedService: SharedService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    if (!this.rxItems.length) {
      this.broshService.getRXItems().subscribe((data) => {
        this.setRxItems();
      });
    } else {
      this.setRxItems();
    }

    if (!this.doctors.length) {
      this.dataService.getDoctorList().subscribe((data) => {
        this.setDoctors();
      });
    } else {
      this.setDoctors();
    }
  }

  setRxItems() {
    this.note.rxItems = this.rxItems.filter((i) =>
      this.note.itemCodes.includes(i.id)
    );
  }

  setDoctors() {
    this.note.doctors = this.doctors.filter((i) =>
      this.note.doctorCodes.includes(i.id)
    );
  }

  public closePanel() {
    this.onCancel.emit();
  }

  saveEnabled() {
    return this.note.cn || this.note.en || this.note.he;
  }

  delete() {
    this.sharedService.emitPopupchReceivedEvent({
      title: "translate.notes.deletePrompt",
      icon: "exclamation-triangle",
      subTitle: this.translateService.instant(
        "translate.notes.deleteValidation"
      ),
      keep: false,
      approve: () => {
        this.save("DELETED");
      },
    });
  }

  deactivate() {
    this.save("INACTIVE");
  }

  saveAndActivate() {
    this.save("ACTIVE");
  }

  save(status?: string) {
    this.saving = true;
    this.broshService
      .updPresentNote(this.note, status ?? this.note.status ?? "PENDING")
      .subscribe(
        (data) => {
          this.saving = false;
          this.onSave.emit();
        },
        (error) => {
          this.saving = false;
          this.sharedService.alertError(error);
        }
      );
  }
}
